import { ChevronRightIcon, PhoneRotaryIcon } from '@forwardflow/icons'
import { usePhoneNumbers } from '../hooks/usePhoneNumbers'
import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import cn from 'classnames'
import { CampaignNumberGroupTabs } from './tabs/CampaignNumberGroupTabs'
import { ChangeRealtorNumberView } from './ChangeRealtorNumberView'
import { ConfirmRealtorNumberChangeModal } from './modals/ConfirmRealtorNumberChangeModal'
import { ActiveNumbersView } from './tabs/ActiveNumbersView'
import { InactiveNumbersView } from './tabs/InactiveNumbersView'
import { IPhoneNumber } from '../interface'

const PhoneNumberGroupMenuItem = ({
   label,
   onClick,
   count,
   isSelected = false,
}: {
   label: string
   onClick: () => void
   count: number
   isSelected: boolean
}) => {
   return (
      <motion.button
         onClick={onClick}
         className={`flex items-center justify-between p-6 pr-3 cursor-pointer w-full ${
            isSelected ? 'bg-[#9061DA]/50' : 'hover:bg-[#9061DA]/10'
         }`}
         whileTap={{ scale: 0.95 }}
      >
         <p className="text-white text-sm font-medium">{label}</p>
         <div className="flex items-center gap-x-2">
            <p
               className={cn(
                  'text-white text-sm font-medium',
                  isSelected ? 'text-white' : 'text-white/70'
               )}
            >
               {count}
            </p>
            <ChevronRightIcon
               className={cn(
                  'text-white w-4 h-4',
                  isSelected ? 'opacity-100' : 'opacity-0'
               )}
            />
         </div>
      </motion.button>
   )
}

export const PhoneNumberManager = () => {
   const {
      activePhoneNumbers,
      inactivePhoneNumbers,
      groupedActivePhoneNumbers,
      groupedInactivePhoneNumbers,
      realtorNumber,
      campaignList,
      useCampaignPhoneNumberGroups,
      updateRealtorNumber,
      isLoadingPhoneNumbers
   } = usePhoneNumbers()

   const [selectedTab, setSelectedTab] = useState<
      'active' | 'inactive' | string
   >('active')
   const [isChangingRealtorNumber, setIsChangingRealtorNumber] = useState(false)
   const [selectedNewNumber, setSelectedNewNumber] =
      useState<IPhoneNumber | null>(null)
   const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
   const [isUpdatingRealtor, setIsUpdatingRealtor] = useState(false)
   const sidebarScrollRef = useRef<HTMLDivElement>(null)
   const [isSidebarScrolled, setIsSidebarScrolled] = useState(false)

   const handleSidebarScroll = () => {
      if (sidebarScrollRef.current) {
         setIsSidebarScrolled(sidebarScrollRef.current.scrollTop > 0)
      }
   }
   const campaignPhoneNumberGroups = useCampaignPhoneNumberGroups(selectedTab)

   const handleSelectRealtorNumber = (number: IPhoneNumber) => {
      setSelectedNewNumber(number)
      setIsConfirmModalOpen(true)
   }

   const handleConfirmChange = async () => {
      if (selectedNewNumber && realtorNumber) {
         setIsUpdatingRealtor(true)
         try {
            await updateRealtorNumber(
               selectedNewNumber.phoneNumber.phoneNumber
            )
            console.log('Successfully updated realtor number')
            setIsConfirmModalOpen(false)
            setIsChangingRealtorNumber(false)
            setSelectedNewNumber(null)
         } catch (error) {
            console.error('Error updating realtor number:', error)
         } finally {
            setIsUpdatingRealtor(false)
         }
      }
   }

   const handleCloseModal = () => {
      if (isUpdatingRealtor) return
      setIsConfirmModalOpen(false)
      setSelectedNewNumber(null)
   }

   useEffect(() => {
      setIsChangingRealtorNumber(false)
   }, [selectedTab])

   if (isLoadingPhoneNumbers) {
      return (
         <div className="rounded-lg bg-[#9061DA]/[0.05] flex w-full max-w-[1064px] h-[calc(100vh-120px)]">
            <div className="w-[350px] shrink-0 bg-[#9061DA]/[0.05] rounded-lg flex flex-col overflow-hidden">
               <div className="flex items-center gap-2 h-20 px-6 shrink-0">
                  <PhoneRotaryIcon className="text-white w-5 h-5 shrink-0" />
                  <p className="text-white text-lg font-medium leading-none">Phone Numbers</p>
               </div>
               <div className="flex flex-col">
                  {[...Array(4)].map((_, i) => (
                     <div
                        key={i}
                        className="flex items-center justify-between p-6 pr-3"
                     >
                        <div className="h-3.5 w-28 rounded bg-white/10 animate-pulse" />
                        <div className="h-3.5 w-6 rounded bg-white/[0.06] animate-pulse" />
                     </div>
                  ))}
               </div>
            </div>
            <div className="flex-1 p-4 pr-0 flex flex-col gap-y-6 overflow-hidden">
               <div className="h-14 w-full rounded bg-white/[0.04] animate-pulse" />
               <div className="flex flex-col gap-y-3">
                  {[...Array(5)].map((_, i) => (
                     <div
                        key={i}
                        className="flex items-center gap-4 p-4 rounded bg-white/[0.03] animate-pulse"
                     >
                        <div className="w-10 h-10 rounded-full bg-white/10 shrink-0" />
                        <div className="flex flex-col gap-1.5 flex-1">
                           <div className="h-3.5 w-40 rounded bg-white/10" />
                           <div className="h-3 w-28 rounded bg-white/[0.06]" />
                        </div>
                     </div>
                  ))}
               </div>
            </div>
         </div>
      )
   }

   return (
      <div className="rounded-lg bg-[#9061DA]/[0.05] flex w-full max-w-[1064px] h-[calc(100vh-120px)]">
         <div className="w-[350px] shrink-0 bg-[#9061DA]/[0.05] rounded-lg flex flex-col overflow-hidden">
            <div className={cn(
               'flex items-center gap-2 h-20 px-6 shrink-0 transition-shadow duration-200',
               isSidebarScrolled && 'shadow-[0_4px_12px_rgba(0,0,0,0.3)]'
            )}>
               <PhoneRotaryIcon className="text-white w-5 h-5 shrink-0" />
               <p className="text-white text-lg font-medium leading-none">Phone Numbers</p>
            </div>
            <div
               ref={sidebarScrollRef}
               onScroll={handleSidebarScroll}
               className="flex-1 overflow-y-auto"
            >
               <PhoneNumberGroupMenuItem
                  label="Active Numbers"
                  onClick={() => setSelectedTab('active')}
                  count={activePhoneNumbers.length}
                  isSelected={selectedTab === 'active'}
               />
               <PhoneNumberGroupMenuItem
                  label="Inactive Numbers"
                  onClick={() => setSelectedTab('inactive')}
                  count={inactivePhoneNumbers.length}
                  isSelected={selectedTab === 'inactive'}
               />
               {campaignList.map((campaign) => (
                  <PhoneNumberGroupMenuItem
                     key={campaign.campaignId}
                     label={campaign.campaignName}
                     onClick={() => setSelectedTab(campaign.campaignId)}
                     count={campaign.numberCount}
                     isSelected={selectedTab === campaign.campaignId}
                  />
               ))}
            </div>
         </div>
         <div className="p-4 pr-0 flex flex-col gap-y-6 flex-1 overflow-y-auto h-full">
            {(() => {
               switch (selectedTab) {
                  case 'active':
                     return isChangingRealtorNumber ? (
                        <ChangeRealtorNumberView
                           activePhoneNumbers={
                              activePhoneNumbers as IPhoneNumber[]
                           }
                           currentRealtorNumber={realtorNumber as IPhoneNumber}
                           onSelectNumber={handleSelectRealtorNumber}
                           onCancel={() => setIsChangingRealtorNumber(false)}
                        />
                     ) : (
                        <ActiveNumbersView
                           realtorNumber={realtorNumber as IPhoneNumber}
                           groupedActivePhoneNumbers={groupedActivePhoneNumbers}
                           onChangeRealtorNumber={() =>
                              setIsChangingRealtorNumber(true)
                           }
                        />
                     )
                  case 'inactive':
                     return (
                        <InactiveNumbersView
                           groupedInactivePhoneNumbers={
                              groupedInactivePhoneNumbers
                           }
                        />
                     )
                  default:
                     return (
                        <CampaignNumberGroupTabs
                           numberGroups={campaignPhoneNumberGroups}
                           campaignId={selectedTab}
                        />
                     )
               }
            })()}
         </div>

         {/* Confirmation Modal */}
         {selectedNewNumber && (
            <ConfirmRealtorNumberChangeModal
               isOpen={isConfirmModalOpen}
               onClose={handleCloseModal}
               onConfirm={handleConfirmChange}
               oldNumber={realtorNumber as IPhoneNumber}
               newNumber={selectedNewNumber}
               isLoading={isUpdatingRealtor}
            />
         )}
      </div>
   )
}
